import {
  BOARDS_ROUTES,
  PROJECT_RESOURCES_DETAILS,
  PROJECT_TEACHER_VIEW_APIS,
  USER_ROUTES,
} from '@/constants';
import {
  mapExternalProject,
  normalizeProjectDetailsResponse,
  toProjectResponseArray,
} from '@/lib';
import { axiosBaseQuery } from '@/services/axios-base-query';
import type {
  AssignedInternalProject,
  CreateInternalProjectRequest,
  CreateMilestoneRequest,
  CreateTaskRequest,
  ExternalProjectApiResponse,
  KanbanProjectResponse,
  MilestoneRaw,
  NewProjectDuuplicateResponse,
  ProjectResourceResponse,
  ProjectResourcesDocs,
  ProjectResponse,
  PubliclyAssignedProjectApiResponse,
  Board,
  EligibleUser,
  AddUsersToProjectPayload,
  CurrentProjectMember,
  Forum,
  ForumResponse,
  CreateForumRequest,
  CreateForumCommentRequest,
  UpdateForumRequest,
  UpdateForumCommentRequest,
  ForumComment,
  ProjectNote,
  CreateProjectNoteRequest,
  DocComment,
  CreateDocCommentRequest,
  UpdateDocCommentRequest,
  ProjectDoc,
  MilestoneDeliverable,
  // new response shapes used by endpoints
  MilestoneCreateResponse,
  TaskResponse,
  DeliverableItem,
  DeliverableComment,
  TaskDocumentUploadResponse,
  ProjectSkillResponse,
  ProjectSkillPayload,
  InternalProjectSkillResponse,
  RequiredEvaluationResponse,
  ClientTemplateResponse,
  EvaluatePeerRequest,
  EvaluatePeerResponse,
  ProgressRound,
  ProgressPerEvaluation,
  UserEvaluationReport,
  ClientUserListItem,
} from '@/types';
import type { AssignProjectDetails } from '@/types/components-types/assign-project-details.types';
import { createApi } from '@reduxjs/toolkit/query/react';

export const projectAPI = createApi({
  reducerPath: 'projectAPI',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Projects',
    'CreateInternalProject',
    'ExternalProjects',
    'Milestones',
    'detailedApplied',
    'projectDetails',
    'projectResources',
    'ProjectMembers',
    'Forums',
    'ProjectNotes',
    'ProjectDocs',
    'ClientTemplate',
    'MilestoneDeliverables',
    'DeliverableComments',
  ] as const,
  endpoints: (builder) => ({
    getInternalProjects: builder.query<ProjectResponse[], { tenant: string; board?: number }>({
      query: ({ tenant, board }) => ({
        url: USER_ROUTES.GET_INTERNAL_PROJECTS_OVERVIEW(tenant, board),
        method: 'GET',
      }),
      transformResponse: (
        response: ProjectResponse[] | AssignedInternalProject[]
      ) => {
        // If it's the "assigned" format, unwrap project_details
        if (
          Array.isArray(response) &&
          response.length > 0 &&
          'project_details' in response[0]
        ) {
          return (response as AssignedInternalProject[]).map((x) => ({
            ...x.project_details,
            project_resorces_id: x.id,
          }));
        }
        // Otherwise it’s already the project list format
        return response as ProjectResponse[];
      },

      providesTags: (result) =>
        result
          ? [
            { type: 'Projects' as const, id: 'LIST' },
            ...result.map((p) => ({ type: 'Projects' as const, id: p.id })),
          ]
          : [{ type: 'Projects' as const, id: 'LIST' }],
    }),
    getInternalProjectsDetailedApplied: builder.query<
      ProjectResponse[],
      { tenant: string; isTeacher?: boolean }
    >({
      query: ({ tenant, isTeacher }) => ({
        url: isTeacher
          ? PROJECT_TEACHER_VIEW_APIS.GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_TEACHER(
            tenant
          )
          : USER_ROUTES.GET_INTERNAL_PROJECTS_DETAILED_APPLIED(tenant),
        method: 'GET',
      }),
      // API can return either the legacy ProjectResponse[] or the newer AssignedInternalProject[]
      transformResponse: (
        response: AssignedInternalProject[] | ProjectResponse[] | unknown
      ) => {
        // if returned items look like the detailed-applied shape, convert them,
        // otherwise trust that they're already ProjectResponse[].
        if (
          Array.isArray(response) &&
          response.length > 0 &&
          'project_details' in response[0]
        ) {
          return toProjectResponseArray(response as AssignedInternalProject[]);
        }
        return response as ProjectResponse[];
      },
      providesTags: (result) =>
        result
          ? [
            { type: 'detailedApplied' as const, id: 'LIST' },
            ...result.map((p) => ({
              type: 'detailedApplied' as const,
              id: p.id,
            })),
            { type: 'Projects' as const, id: 'LIST' }, // Also binds to Projects list
          ]
          : [{ type: 'detailedApplied' as const, id: 'LIST' }],
    }),
    getInternalProjectsDetailedResources: builder.query<
      AssignProjectDetails,
      { tenant: string; id: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_PROJECT_ID(
          tenant,
          id
        ),
        method: 'GET',
      }),
      providesTags: ['Milestones', 'Projects'],
    }),
    getInternalProjectDetailsByProjectId: builder.query<
      ProjectResponse,
      {
        tenant: string;
        projectId: number;
        isTeacher?: boolean;
        isAssigned?: boolean;
      }
    >({
      query: ({ tenant, projectId, isTeacher, isAssigned }) => ({
        url:
          isTeacher && !isAssigned ? USER_ROUTES.GET_INTERNAL_PROJECT_DETAILS_BY_PROJECT_ID(
            tenant,
            projectId
          ) :
            isTeacher && isAssigned
              ? PROJECT_TEACHER_VIEW_APIS.GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_TEACHER_ID(
                tenant,
                projectId
              )
              : USER_ROUTES.GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_PROJECT_ID(
                tenant,
                projectId
              ),
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        // Only normalize if it looks like the new nested format (e.g. has project_details)
        if (response && typeof response === 'object') {
          const r = response as Record<string, unknown>;
          if ('project_details' in r || 'project' in r) {
            return normalizeProjectDetailsResponse(r as unknown);
          }
        }
        return response as ProjectResponse;
      },
      providesTags: (result, __, { projectId }) => {
        const tags: { type: 'projectDetails' | 'Projects'; id: number }[] = [
          { type: 'projectDetails', id: projectId },
          { type: 'Projects', id: projectId },
        ];
        if (result) {
          if (result.project_id && result.project_id !== projectId) {
            tags.push({ type: 'projectDetails', id: result.project_id });
            tags.push({ type: 'Projects', id: result.project_id });
          }
          if (
            result.id &&
            result.id !== projectId &&
            result.id !== result.project_id
          ) {
            tags.push({ type: 'projectDetails', id: result.id });
            tags.push({ type: 'Projects', id: result.id });
          }
        }
        return tags;
      },
    }),
    createInternalProject: builder.mutation<
      ProjectResponse,
      { tenant: string; data: CreateInternalProjectRequest }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.CREATE_INTERNAL_PROJECT(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['CreateInternalProject', 'Projects'],
    }),
    updateInternalProject: builder.mutation<
      ProjectResponse,
      { tenant: string; projectId: number; data: CreateInternalProjectRequest }
    >({
      query: ({ tenant, projectId, data }) => ({
        url: USER_ROUTES.UPDATE_INTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        { type: 'detailedApplied', id: 'LIST' },
        { type: 'Milestones', id: projectId },
      ],
    }),

    deleteInternalProject: builder.mutation<
      ProjectResponse,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.DELETE_INTERNAL_PROJECT(tenant, projectId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects'],
    }),
    updateInternalProjectThumbnail: builder.mutation<
      ProjectResponse,
      {
        tenant: string;
        projectId: number;
        data: FormData;
        assignedInternalProjects?: boolean;
      }
    >({
      query: ({ tenant, projectId, data }) => ({
        url: USER_ROUTES.UPDATE_INTERNAL_PROJECT_THUMBNAIL(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        { type: 'detailedApplied', id: 'LIST' },
      ],
    }),
    duplicateInternalProject: builder.mutation<
      NewProjectDuuplicateResponse,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.DUPLICATE_INTERNAL_PROJECT(tenant),
        method: 'POST',
        data: { old_project_id: projectId },
      }),
      invalidatesTags: ['Projects', 'detailedApplied'],
    }),
    assignInternalProject: builder.mutation<
      ProjectResponse,
      { tenant: string; projectId: number; clientMentorId: string }
    >({
      query: ({ tenant, projectId, clientMentorId }) => ({
        url: USER_ROUTES.ASSIGN_INTERNAL_PROJECT(tenant),
        method: 'POST',
        data: {
          project: projectId,
          user_id: clientMentorId,
        },
      }),
      invalidatesTags: ['Projects', 'detailedApplied'],
    }),
    // External Projects API starts
    getExternalProjects: builder.query<ProjectResponse[], { tenant: string, isCompany: boolean, assignedSchools?: string }>({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const { tenant, isCompany, assignedSchools } = arg;
        const normalizeProjectList = (value: unknown) =>
          (Array.isArray(value) ? value : [])
            .filter((item): item is ExternalProjectApiResponse => Boolean(item));

        try {
          if (isCompany) {
            const result = await baseQuery({
              url: USER_ROUTES.GET_EXTERNAL_PROJECTS(tenant),
              method: 'GET',
            });
            if (result.error) return { error: result.error };
            return {
              data: normalizeProjectList(result.data).map(mapExternalProject)
            };
          } else {
            const result = await baseQuery({
              url: USER_ROUTES.GET_EXTERNAL_AVAILABLE_PROJECTS(),
              method: 'GET',
              params: assignedSchools ? { assigned_schools: assignedSchools } : undefined,
            });
            if (result.error) return { error: result.error };
            return {
              data: normalizeProjectList(result.data).map(mapExternalProject)
            };
          }
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: ['ExternalProjects'],
    }),
    getExternalProjectDetails: builder.query<ProjectResponse, { tenant: string, id: number, isCompany: boolean, isAssigned?: boolean }>({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const { tenant, id, isCompany, isAssigned } = arg;

        try {
          if (isCompany) {
            const result = await baseQuery({
              url: USER_ROUTES.GET_EXTERNAL_PROJECT_DETAILS(tenant, id),
              method: 'GET',
            });
            if (result.error) return { error: result.error };
            if (!result.data) return { error: { status: 404, data: 'Not found' } };
            return {
              data: mapExternalProject(result.data)
            };
          } else {
            const result = await baseQuery({
              url: isAssigned
                ? USER_ROUTES.GET_EXTERNAL_PUBLICLY_ASSIGNED_PROJECTS(id)
                : USER_ROUTES.GET_EXTERNAL_AVAILABLE_PROJECT_DETAILS(id),
              method: 'GET',
            });
            if (result.error) return { error: result.error };
            let data = result.data as any;
            if (isAssigned && Array.isArray(data)) {
              data = data.length > 0 ? data[0] : null;
            }
            if (!data) return { error: { status: 404, data: 'Not found' } };
            const target = data.project_ref ? { ...data.project_ref, ...data } : data;
            return {
              data: mapExternalProject({ ...target, project_resources_id: data.id })
            };
          }
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: (_result, __, { id }) => [
        { type: 'projectDetails', id },
        { type: 'Projects', id },
        { type: 'ExternalProjects', id },
      ],
    }),
    getExternalProjectMilestones: builder.query<
      MilestoneRaw[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_MILESTONE_POST(),
        method: 'GET',
        params: { project: projectId },
      }),
      providesTags: (result) => [
        ...(result?.map(({ id }) => ({ type: 'Milestones' as const, id })) || []),
        'Milestones',
        'ExternalProjects',
        'projectDetails',
      ],
    }),
    getExternalProjectProgress: builder.query<any, { tenant: string; projectId: number }>({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.GET_EXTERNAL_PROJECT_PROGRESS(tenant, projectId),
        method: 'GET',
      }),
      providesTags: (_result, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        'ExternalProjects',
      ],
    }),
    getExternalPubliclyAssignedProjects: builder.query<PubliclyAssignedProjectApiResponse[], { projectId?: number }>({
      query: ({ projectId }) => ({
        url: USER_ROUTES.GET_EXTERNAL_PUBLICLY_ASSIGNED_PROJECTS(projectId),
        method: 'GET',
      }),
      transformResponse: (response: PubliclyAssignedProjectApiResponse | PubliclyAssignedProjectApiResponse[]) =>
        Array.isArray(response) ? response : response ? [response] : [],
      providesTags: (result) => [
        ...(result?.map(({ id }) => ({ type: 'ExternalProjects' as const, id })) || []),
        'ExternalProjects',
      ],
    }),
    createExternalProject: builder.mutation<
      ProjectResponse,
      {
        tenant: string;
        data: {
          name: string;
          project_duration: number;
          project_structure: number;
          project_type: number;
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.CREATE_EXTERNAL_PROJECT(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ExternalProjects'],
    }),
    updateExternalProject: builder.mutation<
      ExternalProjectApiResponse,
      {
        tenant: string;
        projectId: number;
        data: {
          board?: null;
          name?: string;
          project_duration?: number;
          project_structure?: 1 | 2;
          assigned_schools?: number[];
          skill_framework?: number[];
          global_skill_framework?: number[];
          published?: boolean;
        };
        resourceId?: number;
      }
    >({
      query: ({ tenant, projectId, data }) => ({
        url: USER_ROUTES.UPDATE_EXTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId, resourceId }) => {
        const tags: { type: 'ExternalProjects' | 'projectDetails' | 'Projects'; id?: number | string }[] = [
          { type: 'ExternalProjects' },
          { type: 'ExternalProjects', id: projectId },
        ];
        if (resourceId && resourceId !== projectId) {
          tags.push({ type: 'projectDetails', id: resourceId });
          tags.push({ type: 'Projects', id: resourceId });
        }
        return tags;
      },
    }),
    updateThumbnailOfExternalProject: builder.mutation<
      ExternalProjectApiResponse,
      { tenant: string; projectId: number; data: FormData; resourceId?: number }
    >({
      query: ({ tenant, projectId, data }) => ({
        url: USER_ROUTES.UPDATE_EXTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId, resourceId }) => {
        const tags: { type: 'ExternalProjects' | 'projectDetails' | 'Projects'; id?: number | string }[] = [
          { type: 'ExternalProjects' },
          { type: 'ExternalProjects', id: projectId },
        ];
        if (resourceId && resourceId !== projectId) {
          tags.push({ type: 'projectDetails', id: resourceId });
          tags.push({ type: 'Projects', id: resourceId });
        }
        return tags;
      },
    }),
    deleteExternalProject: builder.mutation<
      { message: string },
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.DELETE_EXTERNAL_PROJECT(tenant, projectId),
        method: 'DELETE',
      }),
      invalidatesTags: ['ExternalProjects'],
    }),
    publishExternalProject: builder.mutation<
      { message: string },
      { tenant: string; projectId: number; data: { published: boolean } }
    >({
      query: ({ tenant, projectId, data }) => ({
        url: USER_ROUTES.PUBLISH_EXTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),
    acceptPublicProject: builder.mutation<
      any,
      {
        data: {
          assigned_to: string;
          created_by: string;
          project_ref: number | string;
          public_project_candidates: { role: number; candidate: string }[];
        };
      }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.ACCEPT_PUBLIC_PROJECT(),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),
    startExternalGroupProject: builder.mutation<
      any,
      { projectId: number; data: { project_status: number } }
    >({
      query: ({ projectId, data }) => ({
        url: USER_ROUTES.START_EXTERNAL_GROUP_PROJECT(projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ExternalProjects' },
        { type: 'projectDetails', id: projectId },
        { type: 'Projects', id: projectId },
      ],
    }),
    updateOrAddInternalProjectSDGS: builder.mutation<
      ProjectResponse,
      {
        tenant: string;
        projectId: number;
        data: {
          sdgs: (number | string)[];
        };
        resourceId?: number;
      }
    >({
      query: ({ tenant, projectId, data, resourceId }) => ({
        url: resourceId
          ? USER_ROUTES.UPDATE_EXTERNAL_PROJECT(tenant, projectId)
          : USER_ROUTES.UPDATE_INTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data: data.sdgs.length > 0 ? { sdgs: data.sdgs } : { sdgs: null },
      }),
      invalidatesTags: (_, __, { projectId, resourceId }) => {
        const tags = [
          { type: 'projectDetails' as const, id: projectId },
          { type: 'Projects' as const, id: projectId },
          { type: 'ExternalProjects' as const, id: projectId },
        ];
        if (resourceId && resourceId !== projectId) {
          tags.push({ type: 'projectDetails' as const, id: resourceId });
          tags.push({ type: 'Projects' as const, id: resourceId });
          tags.push({ type: 'ExternalProjects' as const, id: resourceId });
        }
        return tags;
      },
    }),
    updateInternalProjectInfos: builder.mutation<
      ProjectResponse,
      {
        tenant: string;
        projectId: number;
        data: Partial<ProjectResponse>;
        resourceId?: number;
      }
    >({
      query: ({ tenant, projectId, data, resourceId }) => ({
        url: resourceId
          ? USER_ROUTES.UPDATE_EXTERNAL_PROJECT(tenant, projectId)
          : USER_ROUTES.UPDATE_INTERNAL_PROJECT(tenant, projectId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { projectId, resourceId }) => {
        const tags = [
          { type: 'projectDetails' as const, id: projectId },
          { type: 'Projects' as const, id: projectId },
          { type: 'ExternalProjects' as const, id: projectId },
        ];
        if (resourceId && resourceId !== projectId) {
          tags.push({ type: 'projectDetails' as const, id: resourceId });
          tags.push({ type: 'Projects' as const, id: resourceId });
          tags.push({ type: 'ExternalProjects' as const, id: resourceId });
        }
        return tags;
      },
    }),
    getInternalProjectTasks: builder.query<
      KanbanProjectResponse[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.GET_INTERNAL_PROJECT_TASKS(tenant, projectId),
        method: 'GET',
      }),
      providesTags: (result, __, { projectId }) => {
        const tags: { type: 'Milestones'; id: number }[] = [
          { type: 'Milestones', id: projectId },
        ];
        if (result && result.length > 0) {
          const first = result[0];
          if (first.project?.id && first.project.id !== projectId) {
            tags.push({ type: 'Milestones', id: first.project.id });
          }
          if (
            first.id &&
            first.id !== projectId &&
            first.id !== first.project?.id
          ) {
            tags.push({ type: 'Milestones', id: first.id });
          }
        }
        return tags;
      },
    }),
    addOrUpdateMilestone: builder.mutation<
      MilestoneCreateResponse,
      {
        tenant: string;
        data: CreateMilestoneRequest;
      }
    >({
      query: ({ tenant, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.ADD_OR_UPDATE_MILESTON(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, _, { data }) => [
        { type: 'Milestones', id: data.project },
        { type: 'projectDetails', id: data.project },
        { type: 'Milestones', id: result?.project },
        { type: 'projectDetails', id: result?.project },
        'detailedApplied',
      ],
    }),

    deleteMilestone: builder.mutation<
      MilestoneCreateResponse,
      {
        tenant: string;
        id: number;
      }
    >({
      query: ({ tenant, id }) => ({
        url: PROJECT_RESOURCES_DETAILS.DELETE_MILESTONES(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        { type: 'projectDetails', id: result?.project },
        'Milestones',
        'projectDetails',
        'detailedApplied',
      ],
    }),
    updateMilestone: builder.mutation<
      MilestoneRaw,
      {
        tenant: string;
        id: number;
        data: Partial<CreateMilestoneRequest>;
      }
    >({
      query: ({ tenant, id, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.UPDATE_MILESTONE(tenant, id),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        { type: 'projectDetails', id: result?.project },
        'Milestones',
        'projectDetails',
        'detailedApplied',
      ],
    }),
    duplicatedProjectMilestone: builder.mutation<
      MilestoneCreateResponse,
      {
        tenant: string;
        old_id: number;
      }
    >({
      query: ({ tenant, old_id }) => ({
        url: PROJECT_RESOURCES_DETAILS.DUPLICATE_MILESTONE(tenant),
        method: 'POST',
        data: { old_id },
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        { type: 'projectDetails', id: result?.project },
        'Milestones',
        'projectDetails',
        'detailedApplied',
      ],
    }),
    getInternalProjectResources: builder.query<
      ProjectResourceResponse,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: PROJECT_RESOURCES_DETAILS.PROJECT_RESOURCES_GET(tenant, projectId),
        method: 'GET',
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'projectResources', id: projectId },
      ],
    }),
    uploadInternalProjectResource: builder.mutation<
      ProjectResourcesDocs,
      {
        tenant: string;
        data: FormData;
      }
    >({
      query: ({ tenant, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.PROJECT_RESOURCES_POST(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, _, { data }) => [
        {
          type: 'projectResources',
          id: result?.project ?? Number(data.get('project') ?? 0),
        },
        'projectResources',
      ],
    }),
    uploadExternalProjectResource: builder.mutation<
      ProjectResourcesDocs,
      {
        tenant: string;
        data: FormData;
      }
    >({
      query: ({ tenant, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_PROJECT_FILE_RESOURCES_POST(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _, { data }) => [
        { type: 'ExternalProjects' },
        { type: 'projectDetails', id: Number(data.get('project') ?? 0) },
      ],
    }),
    deleteExternalProjectResource: builder.mutation<
      void,
      { tenant: string; id: number }
    >({
      query: ({ tenant, id }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_PROJECT_FILE_RESOURCES_DELETE(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),
    createProjectTask: builder.mutation<
      TaskResponse,
      {
        tenant: string;
        data: CreateTaskRequest;
      }
    >({
      query: ({ tenant, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.CREATE_MILESTONE_TASK(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        { type: 'projectDetails', id: result?.project },
        'Milestones',
        'projectDetails',
        'detailedApplied',
      ],
    }),
    updateProjectTask: builder.mutation<
      TaskResponse,
      {
        tenant: string;
        taskId: number;
        data: Partial<CreateTaskRequest> & { status?: number };
      }
    >({
      query: ({ tenant, taskId, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.UPDATE_MILESTONE_TASK(tenant, taskId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        'Milestones',
        'projectDetails',
      ],
    }),
    deleteProjectTask: builder.mutation<
      TaskResponse,
      {
        tenant: string;
        taskId: number;
      }
    >({
      query: ({ tenant, taskId }) => ({
        url: PROJECT_RESOURCES_DETAILS.DELETE_MILESTONE_TASK(tenant, taskId),
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        'Milestones',
        'projectDetails',
      ],
    }),
    createExternalMilestone: builder.mutation<
      any,
      {
        data: CreateMilestoneRequest;
      }
    >({
      query: ({ data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_MILESTONE_POST(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result: any) => [
        'ExternalProjects',
        { type: 'projectDetails', id: result?.project },
      ],
    }),
    updateExternalMilestone: builder.mutation<
      any,
      {
        id: number;
        data: Partial<CreateMilestoneRequest>;
      }
    >({
      query: ({ id, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_MILESTONE_PATCH(id),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result: any) => [
        'ExternalProjects',
        { type: 'projectDetails', id: result?.project },
      ],
    }),
    deleteExternalMilestone: builder.mutation<
      void,
      {
        id: number;
      }
    >({
      query: ({ id }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_MILESTONE_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),

    createExternalTask: builder.mutation<
      any,
      {
        data: CreateTaskRequest;
      }
    >({
      query: ({ data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_TASK_POST(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result: any) => [
        'ExternalProjects',
        { type: 'projectDetails', id: result?.project_milestones },
      ],
    }),
    updateExternalTask: builder.mutation<
      any,
      {
        id: number;
        data: Partial<CreateTaskRequest> & { status?: number };
      }
    >({
      query: ({ id, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_TASK_PATCH(id),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),
    deleteExternalTask: builder.mutation<
      void,
      {
        id: number;
      }
    >({
      query: ({ id }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_TASK_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ExternalProjects', 'projectDetails'],
    }),
    duplicateProjectTask: builder.mutation<
      TaskResponse,
      {
        tenant: string;
        taskId: number;
      }
    >({
      query: ({ tenant, taskId }) => ({
        url: PROJECT_RESOURCES_DETAILS.DUPLICATE_MILESTONE_TASK(tenant),
        method: 'POST',
        data: { old_id: taskId },
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        'Milestones',
        'projectDetails',
      ],
    }),
    addTaskDocument: builder.mutation<
      TaskDocumentUploadResponse,
      {
        tenant: string;
        taskId: number;
        data: FormData;
      }
    >({
      query: ({ tenant, taskId, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.ADD_TASK_DOCUMENT(tenant, taskId),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Milestones'],
    }),
    deleteTaskDocument: builder.mutation<
      TaskDocumentUploadResponse,
      {
        tenant: string;
        docId: number;
      }
    >({
      query: ({ tenant, docId }) => ({
        url: PROJECT_RESOURCES_DETAILS.DELETE_TASK_DOCUMENT(tenant, docId),
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones' as const, id: result?.project },
        'Milestones',
      ],
    }),
    addDeliverable: builder.mutation<
      DeliverableItem,
      {
        tenant: string;
        data: FormData;
      }
    >({
      query: ({ tenant, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.ADD_DELIVERABLE(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones' as const, id: result?.project },
        'Milestones',
      ],
    }),
    deleteInternalProjectResource: builder.mutation<
      void,
      {
        tenant: string;
        id: number;
      }
    >({
      query: ({ tenant, id }) => ({
        url: PROJECT_RESOURCES_DETAILS.PROJECT_RESOURCES_DELETE(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'projectResources', id },
        'projectResources',
      ],
    }),
    getMilestoneDeliverables: builder.query<
      DeliverableItem[],
      { tenant: string; milestoneId: number }
    >({
      query: ({ tenant, milestoneId }) => ({
        url: PROJECT_RESOURCES_DETAILS.GET_MILESTONE_DELIVERABLES(
          tenant,
          milestoneId
        ),
        method: 'GET',
      }),
      providesTags: ['Milestones'],
    }),
    deleteMilestoneDeliverable: builder.mutation<
      void,
      {
        tenant: string;
        deliverableId: number;
      }
    >({
      query: ({ tenant, deliverableId }) => ({
        url: PROJECT_RESOURCES_DETAILS.DELETE_MILESTONE_DELIVERABLE(
          tenant,
          deliverableId
        ),
        method: 'DELETE',
      }),
      invalidatesTags: ['Milestones'],
    }),
    addDeliverableComment: builder.mutation<
      DeliverableComment,
      {
        tenant: string;
        deliverableId: number;
        resourceId: number;
        comment: string;
      }
    >({
      query: ({ tenant, deliverableId, resourceId, comment }) => ({
        url: PROJECT_RESOURCES_DETAILS.ADD_DELIVERABLE_COMMENT(tenant),
        method: 'POST',
        data: {
          deliverables: deliverableId,
          resource: resourceId,
          comments: comment,
        },
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones' as const, id: result?.project },
        'Milestones',
      ],
    }),
    getDeliverableComments: builder.query<
      DeliverableComment[],
      { tenant: string; deliverableId: number }
    >({
      query: ({ tenant, deliverableId }) => ({
        url: PROJECT_RESOURCES_DETAILS.GET_DELIVERABLE_COMMENTS(
          tenant,
          deliverableId
        ),
        method: 'GET',
      }),
      providesTags: ['Milestones'],
    }),
    deleteDeliverableComment: builder.mutation<
      DeliverableComment,
      {
        tenant: string;
        commentId: number;
      }
    >({
      query: ({ tenant, commentId }) => ({
        url: PROJECT_RESOURCES_DETAILS.DELETE_DELIVERABLE_COMMENT(
          tenant,
          commentId
        ),
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones' as const, id: result?.project },
        'Milestones',
      ],
    }),
    updateDeliverableComment: builder.mutation<
      DeliverableComment,
      {
        tenant: string;
        commentId: number;
        comment: string;
      }
    >({
      query: ({ tenant, commentId, comment }) => ({
        url: PROJECT_RESOURCES_DETAILS.UPDATE_DELIVERABLE_COMMENT(
          tenant,
          commentId
        ),
        method: 'PATCH',
        data: {
          comments: comment,
        },
      }),
      invalidatesTags: (result) => [
        { type: 'Milestones', id: result?.project },
        { type: 'Milestones', id: result?.project },
        'Milestones',
      ],
    }),
    getBoards: builder.query<Board[], { tenant: string; projectId?: number }>({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.GET_BOARDS(tenant, projectId),
        method: 'GET',
      }),
    }),
    getInternalProjectSkills: builder.query<
      InternalProjectSkillResponse[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: `${USER_ROUTES.INTERNAL_PROJECT_SKILLS(tenant)}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
      ],
    }),
    addProjectSkill: builder.mutation<
      ProjectSkillResponse,
      { tenant: string; data: ProjectSkillPayload; projectId: number }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.INTERNAL_PROJECT_SKILLS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        'detailedApplied',
      ],
    }),
    deleteProjectSkill: builder.mutation<
      void,
      { tenant: string; id: number; projectId: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.INTERNAL_PROJECT_SKILL_DETAIL(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        'detailedApplied',
      ],
    }),
    getExternalProjectSkills: builder.query<
      InternalProjectSkillResponse[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: `${USER_ROUTES.EXTERNAL_PROJECT_SKILLS(tenant)}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        { type: 'ExternalProjects', id: projectId },
      ],
    }),
    addExternalProjectSkill: builder.mutation<
      ProjectSkillResponse,
      { tenant: string; data: ProjectSkillPayload; projectId: number }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.EXTERNAL_PROJECT_SKILLS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        { type: 'ExternalProjects', id: projectId },
      ],
    }),
    deleteExternalProjectSkill: builder.mutation<
      void,
      { tenant: string; id: number; projectId: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.EXTERNAL_PROJECT_SKILL_DETAIL(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'projectDetails', id: projectId },
        { type: 'ExternalProjects', id: projectId },
      ],
    }),
    getEligibleUsers: builder.query<
      EligibleUser[],
      { tenant: string; projectId: number; boardId?: number }
    >({
      query: ({ tenant, projectId, boardId }) => ({
        url: USER_ROUTES.GET_ELIGIBLE_USERS(tenant, projectId, boardId),
        method: 'GET',
      }),
    }),
    addUsersToProject: builder.mutation<
      void,
      { tenant: string; data: AddUsersToProjectPayload }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_USERS_TO_PROJECT(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => {
        const tags: (
          | 'detailedApplied'
          | { type: 'ProjectMembers'; id: string | number }
        )[] = ['detailedApplied'];
        // Extract projectId from user_details to invalidate specific project members
        const projectId = data?.user_details?.[0]?.project;
        if (projectId) {
          tags.push({ type: 'ProjectMembers', id: projectId });
        }
        return tags;
      },
    }),
    deleteUserFromProject: builder.mutation<
      void,
      { tenant: string; memberId: string | number; projectListId: number }
    >({
      query: ({ tenant, memberId, projectListId }) => ({
        url: USER_ROUTES.DELETE_USERS_FROM_PROJECT(tenant, projectListId),
        method: 'PUT',
        data: { user_id: memberId },
      }),
      invalidatesTags: (_, __, { projectListId }) => [
        'detailedApplied',
        { type: 'ProjectMembers', id: projectListId },
      ],
    }),
    assignTaskToUser: builder.mutation<
      void,
      {
        tenant: string;
        data: {
          task_uuid: string;
          assigned_to: string;
          task: number;
          schema_id: number;
          schema_name: string;
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ASSIGN_TASK_TO_USER(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Milestones', 'Projects', 'detailedApplied', 'projectDetails'],
    }),
    removeTaskFromUser: builder.mutation<
      void,
      {
        tenant: string;
        data: {
          task_uuid: string;
          assigned_to: string;
          task: number;
          task_id: number;
          schema_id: number;
          schema_name: string;
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.REMOVE_TASK_FROM_USER(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Milestones', 'Projects', 'detailedApplied', 'projectDetails'],
    }),
    getCurrentProjectMembers: builder.query<
      CurrentProjectMember[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.GET_CURRENT_PROJECT_MEMBERS(tenant, projectId),
        method: 'GET',
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
      ],
    }),
    startProject: builder.mutation<void, { tenant: string; projectId: number }>(
      {
        query: ({ tenant, projectId }) => ({
          url: USER_ROUTES.START_PROJECT(tenant, projectId),
          method: 'PATCH',
        }),
        invalidatesTags: (_, __, { projectId }) => [
          { type: 'projectDetails', id: projectId },
          'detailedApplied',
        ],
      }
    ),
    completeProject: builder.mutation<
      void,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.COMPLETE_PROJECT(tenant, projectId),
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        'detailedApplied',
      ],
    }),
    markAsInProgress: builder.mutation<
      void,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.REOPEN_PROJECT(tenant, projectId),
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        'detailedApplied',
      ],
    }),
    getProjectNotes: builder.query<
      ProjectNote[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: `${USER_ROUTES.PROJECT_NOTES(tenant)}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'ProjectNotes' as const,
              id,
            })),
            { type: 'ProjectNotes', id: 'LIST' },
          ]
          : [{ type: 'ProjectNotes', id: 'LIST' }],
    }),
    createProjectNote: builder.mutation<
      ProjectNote,
      { tenant: string; data: CreateProjectNoteRequest }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.PROJECT_NOTES(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'ProjectNotes', id: 'LIST' }],
    }),
    updateProjectNote: builder.mutation<
      ProjectNote,
      {
        tenant: string;
        noteId: number;
        data: Partial<CreateProjectNoteRequest>;
      }
    >({
      query: ({ tenant, noteId, data }) => ({
        url: USER_ROUTES.PROJECT_NOTES_DETAIL(tenant, noteId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { noteId }) => [
        { type: 'ProjectNotes', id: noteId },
      ],
    }),
    shareProjectNote: builder.mutation<
      ProjectNote,
      { tenant: string; noteId: number; personal: boolean }
    >({
      query: ({ tenant, noteId, personal }) => ({
        url: USER_ROUTES.PROJECT_NOTES_DETAIL(tenant, noteId),
        method: 'PATCH',
        data: { personal },
      }),
      invalidatesTags: (_, __, { noteId }) => [
        { type: 'ProjectNotes', id: noteId },
      ],
    }),
    deleteProjectNote: builder.mutation<
      void,
      { tenant: string; noteId: number }
    >({
      query: ({ tenant, noteId }) => ({
        url: USER_ROUTES.PROJECT_NOTES_DETAIL(tenant, noteId),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ProjectNotes', id: 'LIST' }],
    }),
    getExternalProjectNotes: builder.query<
      ProjectNote[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `${USER_ROUTES.EXTERNAL_NOTES()}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { projectId }) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'ProjectNotes' as const,
              id,
            })),
            { type: 'ProjectNotes', id: `EXTERNAL-LIST-${projectId}` },
          ]
          : [{ type: 'ProjectNotes', id: `EXTERNAL-LIST-${projectId}` }],
    }),
    createExternalProjectNote: builder.mutation<
      ProjectNote,
      { data: CreateProjectNoteRequest }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EXTERNAL_NOTES(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        { type: 'ProjectNotes', id: `EXTERNAL-LIST-${data.project}` },
      ],
    }),
    updateExternalProjectNote: builder.mutation<
      ProjectNote,
      {
        noteId: number;
        projectId: number;
        data: Partial<CreateProjectNoteRequest>;
      }
    >({
      query: ({ noteId, data }) => ({
        url: USER_ROUTES.EXTERNAL_NOTES_DETAIL(noteId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { noteId, projectId }) => [
        { type: 'ProjectNotes', id: noteId },
        { type: 'ProjectNotes', id: `EXTERNAL-LIST-${projectId}` },
      ],
    }),
    deleteExternalProjectNote: builder.mutation<
      void,
      { noteId: number; projectId: number }
    >({
      query: ({ noteId }) => ({
        url: USER_ROUTES.EXTERNAL_NOTES_DETAIL(noteId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectNotes', id: `EXTERNAL-LIST-${projectId}` },
      ],
    }),
    getForums: builder.query<
      ForumResponse,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: `${USER_ROUTES.FORUM(tenant)}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { projectId }) =>
        result
          ? [
            { type: 'Forums' as const, id: `LIST-${projectId}` },
            ...result.results.map((f) => ({
              type: 'Forums' as const,
              id: f.id,
            })),
          ]
          : [{ type: 'Forums' as const, id: `LIST-${projectId}` }],
    }),
    createForum: builder.mutation<
      Forum,
      { tenant: string; data: CreateForumRequest }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.FORUM(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        { type: 'Forums', id: `LIST-${data.project}` },
        { type: 'projectDetails', id: data.project },
      ],
    }),
    updateForum: builder.mutation<
      Forum,
      {
        tenant: string;
        forumId: number;
        data: UpdateForumRequest;
        projectId: number;
      }
    >({
      query: ({ tenant, forumId, data }) => ({
        url: USER_ROUTES.FORUM_DETAIL(tenant, forumId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    deleteForum: builder.mutation<
      void,
      { tenant: string; forumId: number; projectId: number }
    >({
      query: ({ tenant, forumId }) => ({
        url: USER_ROUTES.FORUM_DETAIL(tenant, forumId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Forums', id: `LIST-${projectId}` },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    createForumComment: builder.mutation<
      ForumComment,
      { tenant: string; data: CreateForumCommentRequest; projectId: number }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.FORUM_COMMENT(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    updateForumComment: builder.mutation<
      ForumComment,
      {
        tenant: string;
        commentId: number;
        data: UpdateForumCommentRequest;
        projectId: number;
      }
    >({
      query: ({ tenant, commentId, data }) => ({
        url: USER_ROUTES.FORUM_COMMENT_DETAIL(tenant, commentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    deleteForumComment: builder.mutation<
      void,
      { tenant: string; commentId: number; projectId: number }
    >({
      query: ({ tenant, commentId }) => ({
        url: USER_ROUTES.FORUM_COMMENT_DETAIL(tenant, commentId),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    startEvaluation: builder.mutation<
      RequiredEvaluationResponse,
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.REQUIRED_EVALUATION(tenant, projectId),
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        { type: 'Projects', id: projectId },
      ],
    }),
    getClientTemplate: builder.query<
      ClientTemplateResponse,
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_CLIENT_TEMPLATE(tenant),
        method: 'GET',
      }),
      providesTags: ['ClientTemplate'],
    }),
    evaluatePeers: builder.mutation<
      EvaluatePeerResponse[],
      { tenant: string; data: EvaluatePeerRequest[] }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.EVALUATE_PEERS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => {
        if (result && result.length > 0) {
          return [{ type: 'projectDetails', id: result[0].evaluation_project }];
        }
        return [];
      },
    }),
    startExternalEvaluation: builder.mutation<
      any,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: USER_ROUTES.START_EXTERNAL_EVALUATION(projectId),
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'projectDetails', id: projectId },
        { type: 'ExternalProjects', id: projectId },
      ],
    }),
    evaluateExternalPeers: builder.mutation<
      any,
      { data: EvaluatePeerRequest[] }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EVALUATE_EXTERNAL_PEERS(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result) => {
        if (result && result.length > 0) {
          return [
            { type: 'projectDetails', id: result[0].evaluation_project },
            { type: 'ExternalProjects', id: result[0].evaluation_project },
          ];
        }
        return [];
      },
    }),
    getProjectDocs: builder.query<
      ProjectDoc[],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: `${USER_ROUTES.PROJECT_DOCS(tenant)}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { projectId }) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'ProjectDocs' as const, id })),
            { type: 'ProjectDocs', id: `LIST-${projectId}` },
          ]
          : [{ type: 'ProjectDocs', id: `LIST-${projectId}` }],
    }),
    createProjectDoc: builder.mutation<
      ProjectDoc,
      { tenant: string; data: FormData; projectId: number }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.PROJECT_DOCS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectDocs', id: `LIST-${projectId}` },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    updateProjectDoc: builder.mutation<
      ProjectDoc,
      { tenant: string; docId: number; data: FormData; projectId: number }
    >({
      query: ({ tenant, docId, data }) => ({
        url: USER_ROUTES.PROJECT_DOCS_DETAIL(tenant, docId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { docId, projectId }) => [
        { type: 'ProjectDocs', id: docId },
        { type: 'ProjectDocs', id: `LIST-${projectId}` },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    deleteProjectDoc: builder.mutation<
      void,
      { tenant: string; docId: number; projectId: number }
    >({
      query: ({ tenant, docId }) => ({
        url: USER_ROUTES.PROJECT_DOCS_DETAIL(tenant, docId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectDocs', id: `LIST-${projectId}` },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    getDocComments: builder.query<
      DocComment[],
      { tenant: string; docId: number }
    >({
      query: ({ tenant, docId }) => ({
        url: `${USER_ROUTES.PROJECT_DOCS_COMMENTS(tenant)}?resource=${docId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { docId }) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'ProjectDocs' as const,
              id: `COMMENT-${id}`,
            })),
            { type: 'ProjectDocs', id: `COMMENTS-LIST-${docId}` },
          ]
          : [{ type: 'ProjectDocs', id: `COMMENTS-LIST-${docId}` }],
    }),
    createDocComment: builder.mutation<
      DocComment,
      { tenant: string; data: CreateDocCommentRequest }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.PROJECT_DOCS_COMMENTS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        { type: 'ProjectDocs', id: `COMMENTS-LIST-${data.resource}` },
      ],
    }),
    updateDocComment: builder.mutation<
      DocComment,
      {
        tenant: string;
        commentId: number;
        data: UpdateDocCommentRequest;
        docId: number;
      }
    >({
      query: ({ tenant, commentId, data }) => ({
        url: USER_ROUTES.PROJECT_DOCS_COMMENTS_DETAIL(tenant, commentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { commentId, docId }) => [
        { type: 'ProjectDocs', id: `COMMENT-${commentId}` },
        { type: 'ProjectDocs', id: `COMMENTS-LIST-${docId}` },
      ],
    }),
    deleteDocComment: builder.mutation<
      void,
      { tenant: string; commentId: number; docId: number }
    >({
      query: ({ tenant, commentId }) => ({
        url: USER_ROUTES.PROJECT_DOCS_COMMENTS_DETAIL(tenant, commentId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { docId }) => [
        { type: 'ProjectDocs', id: `COMMENTS-LIST-${docId}` },
      ],
    }),
    getIndividualProgress: builder.query<
      ProgressRound[],
      { tenant: string; projectId: number; userId: string; schema: string }
    >({
      query: ({ tenant, projectId, userId, schema }) => ({
        url: USER_ROUTES.INDIVIDUAL_PROGRESS(tenant, projectId, userId, schema),
        method: 'GET',
      }),
    }),
    getProjectEvaluationReport: builder.query<
      UserEvaluationReport[][],
      { tenant: string; projectId: number }
    >({
      query: ({ tenant, projectId }) => ({
        url: USER_ROUTES.PROJECT_EVALUATION_REPORT(tenant, projectId),
        method: 'GET',
      }),
    }),
    getIndividualProgressPerEvaluation: builder.query<
      ProgressPerEvaluation[],
      { tenant: string; projectId: number; userId: string; schema: string }
    >({
      query: ({ tenant, projectId, userId, schema }) => ({
        url: USER_ROUTES.INDIVIDUAL_PROGRESS_PER_EVALUATION(
          tenant,
          projectId,
          userId,
          schema
        ),
        method: 'GET',
      }),
    }),
    getAllBoards: builder.query<
      Board[],
      { tenant: string; userId?: string }
    >({
      query: ({ tenant, userId }) => ({
        url: BOARDS_ROUTES.GET_ALL_BOARDS(tenant, userId),
        method: 'GET',
      }),
      providesTags: ['Projects']
    }),
    addProjectToBoard: builder.mutation<
      void, // It return response from backend but we don't need it
      { tenant: string; boardId: number; projectId: number }
    >({
      query: ({ tenant, boardId, projectId }) => ({
        url: BOARDS_ROUTES.ADD_PROJECT_TO_BOARD(tenant, projectId),
        method: 'PATCH',
        data: {
          board: boardId,
        },
      }),
      invalidatesTags: ['Projects']
    }),
    getExternalMilestoneDeliverables: builder.query<
      MilestoneDeliverable[],
      { milestoneId: number }
    >({
      query: ({ milestoneId }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_DELIVERABLES_GET(milestoneId),
        method: 'GET',
      }),
      providesTags: (result, _, { milestoneId }) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'MilestoneDeliverables' as const,
              id,
            })),
            { type: 'MilestoneDeliverables', id: `LIST-${milestoneId}` },
          ]
          : [{ type: 'MilestoneDeliverables', id: `LIST-${milestoneId}` }],
    }),
    addExternalDeliverable: builder.mutation<
      MilestoneDeliverable,
      { data: FormData }
    >({
      query: ({ data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_DELIVERABLES_POST(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        {
          type: 'MilestoneDeliverables',
          id: `LIST-${data.get('milestone')}`,
        },
      ],
    }),
    deleteExternalDeliverable: builder.mutation<
      void,
      { deliverableId: number; milestoneId: number }
    >({
      query: ({ deliverableId }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_DELIVERABLES_DELETE(
          deliverableId
        ),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { deliverableId, milestoneId }) => [
        { type: 'MilestoneDeliverables', id: deliverableId },
        { type: 'MilestoneDeliverables', id: `LIST-${milestoneId}` },
      ],
    }),
    getExternalDeliverableComments: builder.query<
      DeliverableComment[],
      { deliverableId: number }
    >({
      query: ({ deliverableId }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_COMMENTS_GET(deliverableId),
        method: 'GET',
      }),
      providesTags: (result, _, { deliverableId }) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'DeliverableComments' as const,
              id,
            })),
            { type: 'DeliverableComments', id: `LIST-${deliverableId}` },
          ]
          : [{ type: 'DeliverableComments', id: `LIST-${deliverableId}` }],
    }),
    addExternalDeliverableComment: builder.mutation<
      DeliverableComment,
      { deliverableId: number; data: { comments: string; deliverables: number } }
    >({
      query: ({ data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_COMMENTS_POST(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        {
          type: 'DeliverableComments',
          id: `LIST-${data.deliverables}`,
        },
      ],
    }),
    deleteExternalDeliverableComment: builder.mutation<
      void,
      { commentId: number; deliverableId: number }
    >({
      query: ({ commentId }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_COMMENTS_DELETE(commentId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { commentId, deliverableId }) => [
        { type: 'DeliverableComments', id: commentId },
        { type: 'DeliverableComments', id: `LIST-${deliverableId}` },
      ],
    }),
    updateExternalDeliverableComment: builder.mutation<
      DeliverableComment,
      {
        commentId: number;
        deliverableId: number;
        data: { comments: string; deliverables: number };
      }
    >({
      query: ({ commentId, data }) => ({
        url: PROJECT_RESOURCES_DETAILS.EXTERNAL_COMMENTS_PATCH(commentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { commentId, deliverableId }) => [
        { type: 'DeliverableComments', id: commentId },
        { type: 'DeliverableComments', id: `LIST-${deliverableId}` },
      ],
    }),

    // ---------- External Project: Client Users List ----------
    getClientUsersList: builder.query<
      ClientUserListItem[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_ALL_CLIENT_USER_LIST(tenant),
        method: 'GET',
      }),
    }),

    // ---------- External Project: Add user to project group ----------
    addUserToExternalProject: builder.mutation<
      unknown,
      { id: string; candidate: string; project: string | number; status: number }
    >({
      query: (data) => ({
        url: USER_ROUTES.PUBLIC_PROJECT_GROUPS(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { project }) => [
        { type: 'ProjectMembers', id: 'LIST' },
        { type: 'projectDetails', id: Number(project) },
        { type: 'ExternalProjects' },
      ],
    }),

    deleteUserFromExternalProject: builder.mutation<
      void,
      { candidateId: number; projectId: number }
    >({
      query: ({ candidateId }) => ({
        url: `${USER_ROUTES.PUBLIC_PROJECT_GROUPS()}${candidateId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectMembers', id: 'LIST' },
        { type: 'projectDetails', id: projectId },
        { type: 'ExternalProjects' },
      ],
    }),

    getExternalForums: builder.query<
      ForumResponse,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `${USER_ROUTES.EXTERNAL_FORUM()}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { projectId }) =>
        result
          ? [
            { type: 'Forums' as const, id: `LIST-${projectId}` },
            ...result.results.map((f) => ({
              type: 'Forums' as const,
              id: f.id,
            })),
          ]
          : [{ type: 'Forums' as const, id: `LIST-${projectId}` }],
    }),
    createExternalForum: builder.mutation<
      Forum,
      { data: CreateForumRequest }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        { type: 'Forums', id: `LIST-${data.project}` },
        { type: 'projectDetails', id: data.project },
      ],
    }),
    updateExternalForum: builder.mutation<
      Forum,
      {
        forumId: number;
        data: UpdateForumRequest;
        projectId: number;
      }
    >({
      query: ({ forumId, data }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM_DETAIL(forumId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    deleteExternalForum: builder.mutation<
      void,
      { forumId: number; projectId: number }
    >({
      query: ({ forumId }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM_DETAIL(forumId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'Forums', id: `LIST-${projectId}` },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    createExternalForumComment: builder.mutation<
      ForumComment,
      { data: CreateForumCommentRequest; projectId: number }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM_COMMENT(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    updateExternalForumComment: builder.mutation<
      ForumComment,
      {
        commentId: number;
        data: UpdateForumCommentRequest;
        projectId: number;
      }
    >({
      query: ({ commentId, data }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM_COMMENT_DETAIL(commentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    deleteExternalForumComment: builder.mutation<
      void,
      { commentId: number; projectId: number }
    >({
      query: ({ commentId }) => ({
        url: USER_ROUTES.EXTERNAL_FORUM_COMMENT_DETAIL(commentId),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => {
        void result;
        void error;
        return [{ type: 'Forums', id: `LIST-${projectId}` }];
      },
    }),
    getExternalProjectDocs: builder.query<
      ProjectDoc[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `${USER_ROUTES.EXTERNAL_DOCS()}?project=${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { projectId }) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'ProjectDocs' as const, id })),
            { type: 'ProjectDocs', id: `EXTERNAL-LIST-${projectId}` },
          ]
          : [{ type: 'ProjectDocs', id: `EXTERNAL-LIST-${projectId}` }],
    }),
    createExternalProjectDoc: builder.mutation<
      ProjectDoc,
      { data: FormData; projectId: number }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectDocs', id: `EXTERNAL-LIST-${projectId}` },
        { type: 'ExternalProjects' },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    deleteExternalProjectDoc: builder.mutation<
      void,
      { docId: number; projectId: number }
    >({
      query: ({ docId }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS_DETAIL(docId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { projectId }) => [
        { type: 'ProjectDocs', id: `EXTERNAL-LIST-${projectId}` },
        { type: 'ExternalProjects' },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    updateExternalProjectDoc: builder.mutation<
      ProjectDoc,
      { docId: number; projectId: number; data: FormData }
    >({
      query: ({ docId, data }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS_DETAIL(docId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { docId, projectId }) => [
        { type: 'ProjectDocs', id: docId },
        { type: 'ProjectDocs', id: `EXTERNAL-LIST-${projectId}` },
        { type: 'ExternalProjects' },
        { type: 'projectDetails', id: projectId },
      ],
    }),
    getExternalDocComments: builder.query<
      DocComment[],
      { docId: number }
    >({
      query: ({ docId }) => ({
        url: `${USER_ROUTES.EXTERNAL_DOCS_COMMENTS()}?resource=${docId}`,
        method: 'GET',
      }),
      providesTags: (result, _, { docId }) =>
        result
          ? [
            ...result.map(({ id }) => ({
              type: 'ProjectDocs' as const,
              id: `EXTERNAL-COMMENT-${id}`,
            })),
            { type: 'ProjectDocs', id: `EXTERNAL-COMMENTS-LIST-${docId}` },
          ]
          : [{ type: 'ProjectDocs', id: `EXTERNAL-COMMENTS-LIST-${docId}` }],
    }),
    createExternalDocComment: builder.mutation<
      DocComment,
      { data: CreateDocCommentRequest }
    >({
      query: ({ data }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS_COMMENTS(),
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, { data }) => [
        { type: 'ProjectDocs', id: `EXTERNAL-COMMENTS-LIST-${data.resource}` },
      ],
    }),
    updateExternalDocComment: builder.mutation<
      DocComment,
      {
        commentId: number;
        data: UpdateDocCommentRequest;
        docId: number;
      }
    >({
      query: ({ commentId, data }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS_COMMENTS_DETAIL(commentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_, __, { commentId, docId }) => [
        { type: 'ProjectDocs', id: `EXTERNAL-COMMENT-${commentId}` },
        { type: 'ProjectDocs', id: `EXTERNAL-COMMENTS-LIST-${docId}` },
      ],
    }),
    deleteExternalDocComment: builder.mutation<
      void,
      { commentId: number; docId: number }
    >({
      query: ({ commentId }) => ({
        url: USER_ROUTES.EXTERNAL_DOCS_COMMENTS_DETAIL(commentId),
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { docId }) => [
        { type: 'ProjectDocs', id: `EXTERNAL-COMMENTS-LIST-${docId}` },
      ],
    }),
  }),
});

export const {
  useGetInternalProjectsQuery,
  useGetInternalProjectsDetailedAppliedQuery,
  useGetInternalProjectDetailsByProjectIdQuery,
  useCreateInternalProjectMutation,
  useUpdateInternalProjectMutation,
  useUpdateInternalProjectThumbnailMutation,
  useDuplicateInternalProjectMutation,
  useAssignInternalProjectMutation,
  useGetExternalProjectsQuery,
  useCreateExternalProjectMutation,
  useUpdateExternalProjectMutation,
  useUpdateThumbnailOfExternalProjectMutation,
  useDeleteExternalProjectMutation,
  usePublishExternalProjectMutation,
  useAcceptPublicProjectMutation,
  useStartExternalGroupProjectMutation,
  useUpdateOrAddInternalProjectSDGSMutation,
  useUpdateInternalProjectInfosMutation,
  useGetInternalProjectTasksQuery,
  useGetInternalProjectsDetailedResourcesQuery,
  useAddOrUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useUpdateMilestoneMutation,
  useDuplicatedProjectMilestoneMutation,
  useGetInternalProjectResourcesQuery,
  useUploadInternalProjectResourceMutation,
  useDeleteInternalProjectResourceMutation,
  useCreateProjectTaskMutation,
  useUpdateProjectTaskMutation,
  useDeleteProjectTaskMutation,
  useDuplicateProjectTaskMutation,
  useAddTaskDocumentMutation,
  useDeleteTaskDocumentMutation,
  useAddDeliverableMutation,
  useGetMilestoneDeliverablesQuery,
  useDeleteMilestoneDeliverableMutation,
  useDeleteInternalProjectMutation,
  useAddDeliverableCommentMutation,
  useGetDeliverableCommentsQuery,
  useDeleteDeliverableCommentMutation,
  useUpdateDeliverableCommentMutation,
  useGetBoardsQuery,
  useGetEligibleUsersQuery,
  useAddUsersToProjectMutation,
  useDeleteUserFromProjectMutation,
  useAssignTaskToUserMutation,
  useRemoveTaskFromUserMutation,
  useGetCurrentProjectMembersQuery,
  useStartProjectMutation,
  useCompleteProjectMutation,
  useGetForumsQuery,
  useCreateForumMutation,
  useUpdateForumMutation,
  useDeleteForumMutation,
  useCreateForumCommentMutation,
  useUpdateForumCommentMutation,
  useDeleteForumCommentMutation,
  useGetProjectNotesQuery,
  useCreateProjectNoteMutation,
  useUpdateProjectNoteMutation,
  useShareProjectNoteMutation,
  useDeleteProjectNoteMutation,
  useGetExternalProjectNotesQuery,
  useCreateExternalProjectNoteMutation,
  useUpdateExternalProjectNoteMutation,
  useDeleteExternalProjectNoteMutation,
  useGetProjectDocsQuery,
  useCreateProjectDocMutation,
  useUpdateProjectDocMutation,
  useDeleteProjectDocMutation,
  useGetDocCommentsQuery,
  useCreateDocCommentMutation,
  useUpdateDocCommentMutation,
  useDeleteDocCommentMutation,
  useMarkAsInProgressMutation,
  useAddProjectSkillMutation,
  useDeleteProjectSkillMutation,
  useGetInternalProjectSkillsQuery,
  useStartEvaluationMutation,
  useGetClientTemplateQuery,
  useEvaluatePeersMutation,
  useStartExternalEvaluationMutation,
  useEvaluateExternalPeersMutation,
  useGetIndividualProgressQuery,
  useGetIndividualProgressPerEvaluationQuery,
  useGetProjectEvaluationReportQuery,
  useGetAllBoardsQuery,
  useAddProjectToBoardMutation,
  useGetExternalProjectDetailsQuery,
  useGetExternalProjectMilestonesQuery,
  useGetExternalProjectProgressQuery,
  useGetExternalPubliclyAssignedProjectsQuery,
  useUploadExternalProjectResourceMutation,
  useDeleteExternalProjectResourceMutation,
  useCreateExternalMilestoneMutation,
  useUpdateExternalMilestoneMutation,
  useDeleteExternalMilestoneMutation,
  useCreateExternalTaskMutation,
  useUpdateExternalTaskMutation,
  useDeleteExternalTaskMutation,
  useGetExternalMilestoneDeliverablesQuery,
  useAddExternalDeliverableMutation,
  useDeleteExternalDeliverableMutation,
  useGetExternalDeliverableCommentsQuery,
  useAddExternalDeliverableCommentMutation,
  useDeleteExternalDeliverableCommentMutation,
  useUpdateExternalDeliverableCommentMutation,
  useGetClientUsersListQuery,
  useAddUserToExternalProjectMutation,
  useDeleteUserFromExternalProjectMutation,
  useGetExternalForumsQuery,
  useCreateExternalForumMutation,
  useUpdateExternalForumMutation,
  useDeleteExternalForumMutation,
  useCreateExternalForumCommentMutation,
  useUpdateExternalForumCommentMutation,
  useDeleteExternalForumCommentMutation,
  useGetExternalProjectDocsQuery,
  useCreateExternalProjectDocMutation,
  useUpdateExternalProjectDocMutation,
  useDeleteExternalProjectDocMutation,
  useGetExternalDocCommentsQuery,
  useCreateExternalDocCommentMutation,
  useUpdateExternalDocCommentMutation,
  useDeleteExternalDocCommentMutation,
  useGetExternalProjectSkillsQuery,
  useAddExternalProjectSkillMutation,
  useDeleteExternalProjectSkillMutation,
} = projectAPI;
